import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { DateRange, DayContentProps, DayPicker } from "react-day-picker";
import { format } from "date-fns";
import { get, useController, useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import moment, { Moment } from "moment";

import "react-day-picker/dist/style.css";
import "./DatePicker.scss";
import Input from "components/v2/Input/Input";
import { ReactComponent as CalendarIcon } from "assets/images/iconsV2/calendar.svg";
import { getHolidays } from "api/services/common";
import { Holiday } from "api/response/common.response";
import { isAbortError } from "helpers/zipcodeGeocode";
import { useReviewFieldErrorClear } from "@RequestFormV2/hooks/useReviewFieldErrorClear";
import { ReviewFieldErrorClearWatcher } from "@RequestFormV2/hooks/ReviewFieldErrorClearWatcher";
import getDatesBasedOnQuickSelection from "./quickSelection";
import { DatePickerProps } from "./DatePicker.types";

type HolidaysCache = Record<string, Record<number, Record<number, Holiday[]>>>;

function cacheHolidayMonth(
  prev: HolidaysCache,
  countryCode: string,
  year: number,
  month: number,
  data: Holiday[],
): HolidaysCache {
  return {
    ...prev,
    [countryCode]: {
      ...prev[countryCode],
      [year]: {
        ...prev[countryCode]?.[year],
        [month]: data,
      },
    },
  };
}

function HolidayDayContent(props: DayContentProps & { holidays: HolidaysCache; countryCode: string }) {
  const { date, activeModifiers, holidays, countryCode } = props;
  const intl = useIntl();

  const currentDateHolidayData = useMemo(() => {
    const month = moment(date).month() + 1;
    const year = moment(date).year();
    return holidays?.[countryCode]?.[year]?.[month]?.find(
      (item) => item.date === moment(date).format("YYYY-MM-DD"),
    );
  }, [holidays, date, countryCode]);

  if (activeModifiers?.holiday && currentDateHolidayData) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`date-picker-holiday-${moment(date).format("YYYY-MM-DD")}`} style={{ zIndex: 999999 }}>
            <span className="font-weight-normal">
              {intl.formatMessage(
                {
                  id: "new-request.schedule.holidayWarning",
                  defaultMessage: "Holiday Warning: It might be {name} on {date}",
                },
                {
                  name: currentDateHolidayData.name || "",
                  date: moment(date).format("MMM D"),
                },
              )}
            </span>
          </Tooltip>
        }
      >
        <div>
          <div className="date-picker-holiday-indicator" />
          <span>{moment(date).format("D")}</span>
        </div>
      </OverlayTrigger>
    );
  }

  return <span>{moment(date).format("D")}</span>;
}

const quickSelectionOptions = [
  { id: "general.today", defaultMessage: "Today", value: "today" },
  { id: "general.yesterday", defaultMessage: "Yesterday", value: "yesterday" },
  { id: "general.thisWeek", defaultMessage: "This week", value: "this_week" },
  { id: "general.lastWeek", defaultMessage: "Last week", value: "last_week" },
  { id: "general.thisMonth", defaultMessage: "This month", value: "this_month" },
  { id: "general.lastMonth", defaultMessage: "Last month", value: "last_month" },
  { id: "general.thisYear", defaultMessage: "This year", value: "this_year" },
  { id: "general.lastYear", defaultMessage: "Last year", value: "last_year" },
];

/**
 * TODO: Remaining features to be implemented when needed:
 * - Footer
 * - Disabling dates
 * - 1 month view mode designs
 */
const DatePicker: FC<DatePickerProps> = function (props) {
  const {
    selectionMode,
    numberOfMonths = 2,
    alignment = "left",
    enableQuickSelection = false,
    fullWidth = false,
    disabled = false,
    required = false,
    shouldUnregister = false,
    registerOptions = undefined,
    dateFormat = "YYYY-MM-DD",
    validations = undefined,
    name,
    defaultValue = undefined,
    onDateSelect = undefined,
    minDate = undefined,
    maxDate = undefined,
    hasError = false,
    placeholder = undefined,
    testId = undefined,
    openOnFocus = false,
    countryCode = undefined,
  } = props;
  const datePickerRef = useRef<HTMLDivElement>(null);
  const inputTriggerRef = useRef<HTMLDivElement>(null);
  const outerContainerRef = useRef<HTMLDivElement>(null);
  // Tracks whether the current focus originates from a pointer press so we don't
  // double-handle mouse clicks (which already toggle via onClick) when openOnFocus is set.
  const pointerFocusRef = useRef(false);
  const { control } = useFormContext();
  const defaultFieldConfig = {
    control,
    shouldUnregister,
    rules: {
      required,
      validate: { ...validations },
      ...registerOptions,
    },
  };

  // Determine field name and default value based on selection mode
  const fromFieldName = typeof name === "string" ? name : name.from;
  const toFieldName = selectionMode === "range" && typeof name === "object" ? name.to : "";
  const clearFromReviewError = useReviewFieldErrorClear(fromFieldName);
  const clearToReviewError = useReviewFieldErrorClear(toFieldName || undefined);
  const fromDefaultValue =
    defaultValue && typeof defaultValue === "string"
      ? defaultValue
      : isDateRange(defaultValue)
      ? defaultValue.from
      : undefined;
  const toDefaultValue = isDateRange(defaultValue) ? defaultValue.to : undefined;

  // Setup controller for fromField
  const {
    field: fromField,
    formState: { errors },
  } = useController({
    ...defaultFieldConfig,
    name: fromFieldName,
    defaultValue: fromDefaultValue,
  });

  // Setup controller for toField if range mode is enabled
  const toField =
    selectionMode === "range" && toFieldName
      ? useController({
          ...defaultFieldConfig,
          name: toFieldName,
          defaultValue: toDefaultValue,
        }).field
      : null;

  const [month, setMonth] = useState(new Date()); // State for controlling the displayed month of the calendar
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined); // State for Single selection mode
  const [selectedRange, setSelectedRange] = React.useState<DateRange | undefined>(undefined); // State for Range selection mode
  const [inputValue, setInputValue] = useState(""); // State for the input field value
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [activeQuickSelection, setActiveQuickSelection] = useState<string | undefined>();
  const [clearQuickSelection, setClearQuickSelection] = useState(true);
  const [holidays, setHolidays] = useState<HolidaysCache>({});
  const holidaysAbortController = useRef<AbortController>();

  const normalizedCountryCode = countryCode?.trim().toUpperCase() || "";

  const getHolidayData = (date: Date) => {
    if (!normalizedCountryCode) {
      return;
    }
    const holidayMonth = moment(date).month() + 1;
    const year = moment(date).year();
    if (holidaysAbortController.current) {
      holidaysAbortController.current.abort();
    }
    if (holidays?.[normalizedCountryCode]?.[year]?.[holidayMonth]) {
      return;
    }
    holidaysAbortController.current = new AbortController();
    getHolidays({
      params: {
        country: normalizedCountryCode,
        year,
        month: holidayMonth,
        language: "en",
      },
      signal: holidaysAbortController.current.signal,
    })
      .then((res) => {
        setHolidays((prev) =>
          cacheHolidayMonth(prev, normalizedCountryCode, year, holidayMonth, res.data ?? []),
        );
      })
      .catch((err) => {
        if (isAbortError(err)) {
          return;
        }
        if (err?.response?.status === 404) {
          setHolidays((prev) => cacheHolidayMonth(prev, normalizedCountryCode, year, holidayMonth, []));
        }
      });
  };

  const getHolidayForDay = (day: Date) => {
    if (!normalizedCountryCode) {
      return undefined;
    }
    const holidayMonth = moment(day).month() + 1;
    const year = moment(day).year();
    const monthHolidays = holidays?.[normalizedCountryCode]?.[year]?.[holidayMonth];
    if (!monthHolidays) {
      return undefined;
    }
    return monthHolidays.find((item) => item.date === moment(day).format("YYYY-MM-DD"));
  };

  const handleMonthChange = (nextMonth: Date) => {
    setMonth(nextMonth);
    if (normalizedCountryCode) {
      getHolidayData(nextMonth);
    }
  };

  const inputDateDisplayFormat = "MMM d yyyy"; // Note: This is not the format that is stored in the form

  // When a datepicker is rendered within a drawer, overflow-y is set to visible when open to avoid horizontal clipping
  useEffect(() => {
    const drawerBody = document.querySelector<HTMLElement>(".drawer-body");
    if (drawerBody) drawerBody.style.overflowY = isDatePickerOpen ? "visible" : "auto";
  }, [isDatePickerOpen]);

  // Hook to handle the click outside the date picker dialog
  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        datePickerRef.current?.contains(target) ||
        inputTriggerRef.current?.contains(target)
      ) {
        return;
      }
      setIsDatePickerOpen(false);
    }
    window.addEventListener("click", handleOutsideClick);
    return () => window.removeEventListener("click", handleOutsideClick);
  }, []);

  // Sync display state when form field value changes (including programmatic setValue)
  useEffect(() => {
    const setFormattedDate = (date: Moment) => (date.isValid() ? date.format("MMM D yyyy") : "");

    if (selectionMode === "single" && typeof name === "string") {
      const dateString =
        fromField.value || (typeof defaultValue === "string" ? defaultValue : undefined);

      if (!dateString) {
        setInputValue("");
        setSelectedDate(undefined);
        return;
      }

      const date = moment(dateString, dateFormat, true);
      if (date.isValid()) {
        setInputValue(date.format("MMM D yyyy"));
        setSelectedDate(date.toDate());
        setMonth(date.toDate());
      } else {
        setInputValue(dateString);
        setSelectedDate(undefined);
      }
    } else if (selectionMode === "range" && typeof name === "object") {
      const fromDate = moment(fromField.value || fromDefaultValue, dateFormat, true);
      const toDate = moment(toField?.value || toDefaultValue, dateFormat, true);

      if (fromDate.isValid() && toDate.isValid()) {
        setInputValue(`${setFormattedDate(fromDate)} - ${setFormattedDate(toDate)}`);
        setSelectedRange({ from: fromDate.toDate(), to: toDate.toDate() });
        setMonth(fromDate.toDate());
      } else if (fromDate.isValid()) {
        setInputValue(setFormattedDate(fromDate));
        setSelectedRange({ from: fromDate.toDate(), to: undefined });
        setMonth(fromDate.toDate());
      } else if (toDate.isValid()) {
        setInputValue(setFormattedDate(toDate));
        setSelectedRange({ from: undefined, to: toDate.toDate() });
        setMonth(toDate.toDate());
      } else {
        setInputValue("");
        setSelectedRange(undefined);
      }
    }
  }, [
    fromField.value,
    toField?.value,
    selectionMode,
    name,
    dateFormat,
    defaultValue,
    fromDefaultValue,
    toDefaultValue,
  ]);

  // Hook to clear the quick selection when the date or range is selected
  useEffect(() => {
    if (clearQuickSelection) setActiveQuickSelection(undefined);
    else {
      setActiveQuickSelection(activeQuickSelection);
      setClearQuickSelection(true);
    }
  }, [selectedDate, selectedRange]);

  // Hook to handle the styling of the last and first cells with buttons in the calendar
  useEffect(() => {
    if (isDatePickerOpen && selectionMode === "range") {
      document.querySelectorAll(".rdp-row").forEach((row) => {
        // Find all .rdp-cell elements in this row containing a button
        const cellsWithButton = Array.from(row.querySelectorAll(".rdp-cell")).filter((cell) => cell.querySelector("button"));

        // If there are any cells with buttons, add a specific class to the last one
        if (cellsWithButton.length > 0) {
          cellsWithButton[0].classList.add("rdp-first-cell-with-button");
          cellsWithButton[cellsWithButton.length - 1].classList.add("rdp-last-cell-with-button");
        }
      });
    }
  }, [isDatePickerOpen, month]);

  // Hook to handle removing the :after from two cells if the dates are next to each other
  useEffect(() => {
    if (!selectedRange?.from || !selectedRange.to) return;
    const diff = Math.abs(selectedRange.from.getTime() - selectedRange.to.getTime());
    const selectedDays = document.querySelectorAll(".rdp-day_selected");
    const action = diff === 86400000 ? "add" : "remove";
    selectedDays.forEach((day) => day.classList[action]("rdp-day_selected_no_after"));
  }, [selectedRange, isDatePickerOpen, month]);

  // Function to toggle the date picker dialog
  const toggleDatePicker = () => {
    if (disabled) return;
    const nextOpen = !isDatePickerOpen;
    setIsDatePickerOpen(nextOpen);
    if (nextOpen && normalizedCountryCode) {
      getHolidayData(month);
    }
  };

  // Mark that focus is being driven by a pointer press. Reset on the next tick so
  // a subsequent keyboard focus (Tab) is not mistaken for a pointer interaction.
  const handlePointerDown = () => {
    if (!openOnFocus) return;
    pointerFocusRef.current = true;
    setTimeout(() => {
      pointerFocusRef.current = false;
    }, 0);
  };

  // Open the calendar when the input gains focus via the keyboard (Tab). Pointer-driven
  // focus is ignored here because the existing onClick handler already toggles the calendar.
  const handleContainerFocus = () => {
    if (!openOnFocus || disabled || pointerFocusRef.current) return;
    setIsDatePickerOpen(true);
    if (normalizedCountryCode) {
      getHolidayData(month);
    }
  };

  // Close the calendar when focus leaves the whole component (e.g. Tab to the next field).
  const handleContainerBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!openOnFocus) return;
    if (!outerContainerRef.current?.contains(e.relatedTarget as Node | null)) {
      setIsDatePickerOpen(false);
    }
  };

  // Hndle the date selection, called from either clicking a date or selecting a range or via the quick selection buttons
  const handleDayPickerSelect = (selected: Date | DateRange | undefined) => {
    const formatSelectedDate = (date: Date) => moment(date).format(dateFormat);

    // Handle date select based on the selection mode
    if (selectionMode === "single") {
      const selectedDate = selected as Date;
      if (selectedDate) {
        setSelectedDate(selectedDate); // For React Date Picker component
        setInputValue(selectedDate ? format(selectedDate, inputDateDisplayFormat) : ""); // For the input field display
        fromField.onChange(formatSelectedDate(selectedDate)); // For React Hook Form

        clearFromReviewError(formatSelectedDate(selectedDate));
        if (onDateSelect) onDateSelect(selectedDate);
      }
    } else if (selectionMode === "range") {
      const selectedRange = selected as DateRange;
      setSelectedRange(selectedRange); // For React Date Picker component

      if (selectedRange?.from && selectedRange.to) {
        const fromFormatted = format(selectedRange.from, inputDateDisplayFormat);
        const toFormatted = format(selectedRange.to, inputDateDisplayFormat);
        setInputValue(`${fromFormatted} - ${toFormatted}`); // For the input field display

        fromField.onChange(formatSelectedDate(selectedRange.from)); // For React Hook Form
        toField?.onChange(formatSelectedDate(selectedRange.to)); // For React Hook Form

        clearFromReviewError(formatSelectedDate(selectedRange.from));
        clearToReviewError(formatSelectedDate(selectedRange.to));

        if (onDateSelect) (onDateSelect as (dateRange: DateRange) => void)(selectedRange); // Callback function if provided
      } else {
        setInputValue("");
      }
    }

    // Only close for single selection mode as the range mode requires the user to click multiple times to select a range
    if (selectionMode === "single" && selected) setIsDatePickerOpen(false);
  };

  // Type guard to check if the value is a DateRange
  function isDateRange(value: any): value is DateRange {
    return value && typeof value === "object" && "from" in value && "to" in value;
  }

  // Function to handle the quick selection click
  const handleQuickSelectionOnClick = (selection: string) => {
    const dateRange = getDatesBasedOnQuickSelection(selection);
    setActiveQuickSelection(selection);
    setClearQuickSelection(false);
    handleDayPickerSelect(dateRange);
    setMonth(dateRange?.from || new Date()); // Set the calendar's displayed month to the start date of the range
  };

  // Common props regardless of the selection mode
  const commonProps = {
    numberOfMonths,
    pagedNavigation: true,
    showOutsideDays: false,
    month,
    onMonthChange: handleMonthChange,
    onSelect: handleDayPickerSelect,
    fromDate: minDate,
    toDate: maxDate,
    ...(normalizedCountryCode
      ? {
          modifiers: {
            holiday: (day: Date) => Boolean(getHolidayForDay(day)),
          },
          components: {
            DayContent: (data: DayContentProps) => (
              <HolidayDayContent {...data} holidays={holidays} countryCode={normalizedCountryCode} />
            ),
          },
        }
      : {}),
  };

  const outerContainerClass = `date-picker-outer-container ${fullWidth ? "date-picker-full-width" : ""}`;
  const calendarContainerClass = `date-picker-calendar date-picker-align-${alignment === "left" ? "left" : "right"}`;
  const quickSelectionButtonClass = (selection: string) =>
    `date-picker-quick-selection-button_${activeQuickSelection === selection ? "active" : ""}`;
  const inputPlaceholder = placeholder ?? (selectionMode === "single" ? "Select date" : "Select dates");
  const fromName = typeof name === "string" ? name : name.from;
  const toName = typeof name === "string" ? "" : name.to;
  const inputId = `date-picker-input-${fromName}`;
  const fieldError = get(errors, fromName) || get(errors, toName);

  return (
    <>
      <ReviewFieldErrorClearWatcher fieldPath={fromFieldName} />
      {toFieldName ? <ReviewFieldErrorClearWatcher fieldPath={toFieldName} /> : null}
      <div
        className={outerContainerClass}
        ref={outerContainerRef}
        onFocus={handleContainerFocus}
        onBlur={handleContainerBlur}
        onMouseDown={handlePointerDown}
      >
      <div className="date-picker-inner-container">
        {/* Date input field */}
        <div ref={inputTriggerRef} onClick={toggleDatePicker}>
          <Input
            id={inputId}
            value={inputValue}
            placeholder={inputPlaceholder}
            disabled={disabled}
            icon={
              <CalendarIcon
                className="date-picker-calendar-icon"
                onMouseDown={(e) => e.preventDefault()}
                onClick={toggleDatePicker}
              />
            }
            readOnly
            hasError={hasError || Boolean(fieldError)}
            testId={testId}
          />
        </div>
        {/* React hook form errors */}
        {fieldError && (
          <div className="d-block text-sm invalid-feedback">
            {get(errors, fromName)?.message
              ? get(errors, fromName).message
              : get(errors, toName)?.message || (
                  <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
                )}
          </div>
        )}
        {/* Date picker */}
        {isDatePickerOpen && (
          <div className={calendarContainerClass} ref={datePickerRef}>
            {/* Quick selection section */}
            {selectionMode === "range" && enableQuickSelection && (
              <div className="date-picker-quick-selection">
                {quickSelectionOptions.map(({ id, defaultMessage, value }) => (
                  <button
                    key={id}
                    onClick={() => handleQuickSelectionOnClick(value)}
                    className={quickSelectionButtonClass(id)}
                    type="button"
                  >
                    <FormattedMessage id={id} defaultMessage={defaultMessage} />
                  </button>
                ))}
              </div>
            )}
            {/* Calendar section */}
            <div>
              {selectionMode === "single" && <DayPicker {...commonProps} mode="single" selected={selectedDate} />}
              {selectionMode === "range" && <DayPicker {...commonProps} mode="range" selected={selectedRange} />}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default DatePicker;
